/**
 * Reports data fetching hooks.
 * 
 * @module hooks/useReports
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { FullReportWithRelations, Response, ReportPerPrompt, PaginatedResponse, ReportQueryOptions } from '../types';

/**
 * Fetches paginated reports list with business and user information.
 * 
 * @param options - Query options
 * @returns Query result with paginated reports
 */
export function useReports(options: ReportQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    businessId,
    userId,
    status,
    dateFrom,
    dateTo,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['reports', options],
    queryFn: async (): Promise<PaginatedResponse<FullReportWithRelations>> => {
      let query = supabase
        .from('full_reports')
        .select(
          '*, businesses!business_id(id, business_name), users!profile_id(id, email, full_name)',
          { count: 'exact' }
        );

      // Apply filters
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      if (userId) {
        query = query.eq('profile_id', userId);
      }

      if (status) {
        query = query.eq('status', status);
      }

      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }

      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch reports: ${error.message}`);
      }

      return {
        data: (data as FullReportWithRelations[]) || [],
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches a single report by ID.
 * 
 * @param reportId - Report ID
 * @returns Query result with report details
 */
export function useReport(reportId: string | undefined) {
  return useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is required');

      const { data, error } = await supabase
        .from('full_reports')
        .select(
          '*, businesses!business_id(id, business_name), users!profile_id(id, email, full_name)'
        )
        .eq('id', reportId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch report: ${error.message}`);
      }

      return data as FullReportWithRelations;
    },
    enabled: !!reportId,
  });
}

/**
 * Fetches responses for a report.
 * 
 * @param reportId - Report ID
 * @returns Query result with responses
 */
export function useReportResponses(reportId: string | undefined) {
  return useQuery({
    queryKey: ['report-responses', reportId],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is required');

      const { data, error } = await supabase
        .from('responses')
        .select('*')
        .eq('run_id', reportId)
        .order('created_at', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch responses: ${error.message}`);
      }

      return (data as Response[]) || [];
    },
    enabled: !!reportId,
  });
}

/**
 * Fetches per-prompt metrics for a report.
 * 
 * @param reportId - Report ID
 * @returns Query result with prompt metrics
 */
export function useReportPromptMetrics(reportId: string | undefined) {
  return useQuery({
    queryKey: ['report-prompt-metrics', reportId],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is required');

      const { data, error } = await supabase
        .from('reports_per_prompt')
        .select('*')
        .eq('run_id', reportId);

      if (error) {
        throw new Error(`Failed to fetch prompt metrics: ${error.message}`);
      }

      return (data as ReportPerPrompt[]) || [];
    },
    enabled: !!reportId,
  });
}
